import React, { useEffect, useState } from "react";
import Asset from "../../../models/asset";
import Document from "../../../models/document";
import DocumentCategory from "../../../models/document-category";
import Subcompany from "../../../models/subcompany";
import { buildNewSpreadsheetDocument } from "./documentSpreadsheetUtils";
import idGenerator from "./idGenerator";

interface UseDocumentRowActionsParams {
  additionalFields: any;
  assets: Asset[];
  companyId: number;
  documentCategories: DocumentCategory[];
  documents: Document[];
  gridApi: any;
  setDocuments: React.Dispatch<React.SetStateAction<Document[]>>;
  subcompanies: Subcompany[];
}

const useDocumentRowActions = ({
  additionalFields,
  assets,
  companyId,
  documentCategories,
  documents,
  gridApi,
  setDocuments,
  subcompanies,
}: UseDocumentRowActionsParams) => {
  const [addRowLoading, setAddRowLoading] = useState(false);

  useEffect(() => {
    return () => { idGenerator.reset(); };
  }, []);

  useEffect(() => {
    if (documents.length === 0 || assets.length === 0) return;
    const updated = documents.map((doc) => {
      const asset = assets.find((a) => a.id === doc.assetId);
      return { ...doc, typeName: asset?.typeName || "" };
    });
    if (updated.some((doc, i) => doc.typeName !== documents[i].typeName)) {
      setDocuments(updated);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets, documents]);

  const handleAddNewRow = (position: "top" | "bottom" = "bottom") => {
    setAddRowLoading(true);
    const newDocument = buildNewSpreadsheetDocument({
      additionalFields,
      assets,
      companyId,
      documentCategories,
      nextId: idGenerator.next,
      subcompanies,
    });
    const rowIndex = position === "top" ? 0 : documents.length;
    setDocuments((prev) =>
      position === "top" ? [newDocument, ...prev] : [...prev, newDocument],
    );
    setTimeout(() => {
      if (gridApi) {
        let focused = false;
        const focusNewRow = () => {
          if (focused) return;
          focused = true;
          gridApi.removeEventListener?.("modelUpdated", focusNewRow);
          gridApi.ensureIndexVisible(rowIndex, position);
          gridApi.setFocusedCell(rowIndex, "name");
          gridApi.startEditingCell({ rowIndex, colKey: "name" });
        };
        gridApi.addEventListener?.("modelUpdated", focusNewRow);
        if (position === "top") gridApi.paginationGoToFirstPage?.();
        else gridApi.paginationGoToLastPage?.();
        gridApi.purgeInfiniteCache?.();
        setTimeout(focusNewRow, 1000);
      }
      setAddRowLoading(false);
    }, 500);
  };

  return { addRowLoading, handleAddNewRow };
};

export default useDocumentRowActions;
